import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/lib/AuthContext";
import { Lock, Mail, User, Eye, EyeOff, KeyRound } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH. The redirect_url is derived dynamically from
// window.location.origin and Emergent returns ?session_id=... in the URL hash.
function googleSignIn() {
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function AuthPage() {
  const { login, register, activateGodmode, user } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register | godmode
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [godmodeCode, setGodmodeCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) { navigate("/"); return null; }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    if (mode === "login") {
      const res = await login(email, password);
      if (res.ok) { toast.success("Welcome back"); navigate("/"); }
      else setError(res.error);
    } else if (mode === "register") {
      const res = await register(email, password, name);
      if (res.ok) { toast.success("Account created"); navigate("/"); }
      else setError(res.error);
    }
    setLoading(false);
  };

  const handleGodmode = async () => {
    setError(""); setLoading(true);
    const res = await activateGodmode(godmodeCode);
    if (res.ok) {
      toast.success(res.tier === "lifetime" ? "LIFETIME ACCESS UNLOCKED" : "GODMODE activated for 24 hours");
      navigate("/");
    }
    else setError(res.error);
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4" data-testid="auth-page">
      <div className="w-full max-w-sm space-y-6">
        {/* Logo */}
        <div className="text-center space-y-2">
          <img
            src="https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/rbskp5aq_MIRRORS_1024x1024.jpg"
            alt="GODMODE SOUND"
            className="w-16 h-16 mx-auto"
            style={{ filter: "drop-shadow(0 0 12px rgba(255,0,0,0.5))" }}
          />
          <div className="text-lg font-mono font-bold tracking-widest text-[#FF0000]">GODMODE SOUND</div>
          <div className="text-[10px] font-mono tracking-wider text-[#FF0000]/30">PREMIUM AUDIO PLATFORM</div>
        </div>

        {/* Continue with Google */}
        <button
          type="button"
          onClick={googleSignIn}
          className="w-full flex items-center justify-center gap-3 py-2.5 bg-white text-black hover:bg-white/90 transition-colors font-mono text-xs tracking-widest uppercase rounded-sm"
          data-testid="google-signin-btn"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.97 10.97 0 0 0 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.1A6.62 6.62 0 0 1 5.5 12c0-.73.12-1.44.34-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.77.42 3.44 1.18 4.93l3.66-2.83z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.46 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"/>
          </svg>
          Continue with Google
        </button>

        {/* Divider */}
        <div className="flex items-center gap-2">
          <div className="flex-1 h-px bg-[#FF0000]/15" />
          <span className="text-[8px] font-mono tracking-widest text-[#FF0000]/30 uppercase">or</span>
          <div className="flex-1 h-px bg-[#FF0000]/15" />
        </div>

        {/* Tabs */}
        <div className="flex gap-1">
          {["login", "register"].map(m => (
            <button key={m} onClick={() => { setMode(m); setError(""); }}
              className={`flex-1 py-2 text-[10px] font-mono tracking-widest uppercase border transition-all ${
                mode === m ? "border-[#FF0000]/50 bg-[#FF0000]/10 text-[#FF0000]" : "border-[#222] text-[#555] hover:text-[#FF0000]/60"}`}
              data-testid={`auth-tab-${m}`}>{m}</button>
          ))}
        </div>

        {/* Form */}
        <Card className="bg-[#0a0000]/80 border-[#FF0000]/15">
          <CardContent className="p-5">
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === "register" && (
                <div className="relative">
                  <User className="absolute left-3 top-2.5 w-4 h-4 text-[#FF0000]/30" />
                  <Input value={name} onChange={e => setName(e.target.value)} placeholder="Name"
                    className="pl-10 bg-black border-[#FF0000]/20 text-[#FF0000] placeholder:text-[#FF0000]/25 font-mono"
                    data-testid="auth-name" />
                </div>
              )}
              <div className="relative">
                <Mail className="absolute left-3 top-2.5 w-4 h-4 text-[#FF0000]/30" />
                <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email"
                  className="pl-10 bg-black border-[#FF0000]/20 text-[#FF0000] placeholder:text-[#FF0000]/25 font-mono" required
                  data-testid="auth-email" />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-2.5 w-4 h-4 text-[#FF0000]/30" />
                <Input type={showPw ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder="Password"
                  className="pl-10 pr-10 bg-black border-[#FF0000]/20 text-[#FF0000] placeholder:text-[#FF0000]/25 font-mono" required minLength={6}
                  data-testid="auth-password" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-2.5 text-[#FF0000]/30 hover:text-[#FF0000]/60">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {error && <div className="text-[10px] font-mono text-[#FF0000] bg-[#FF0000]/10 border border-[#FF0000]/30 p-2" data-testid="auth-error">{error}</div>}
              <Button type="submit" disabled={loading}
                className="w-full bg-[#FF0000] text-black hover:bg-[#FF0000]/80 font-mono font-bold tracking-wider"
                data-testid="auth-submit">
                {loading ? "..." : mode === "login" ? "SIGN IN" : "CREATE ACCOUNT"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* GODMODE code section */}
        {user && (
        <Card className="bg-[#0a0000]/60 border-[#39FF14]/15">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-[#39FF14]/50" />
              <span className="text-[10px] font-mono tracking-wider text-[#39FF14]/50 uppercase">Access Code</span>
            </div>
            <div className="flex gap-2">
              <Input value={godmodeCode} onChange={e => setGodmodeCode(e.target.value)} placeholder="Enter code..."
                className="bg-black border-[#39FF14]/20 text-[#39FF14] placeholder:text-[#39FF14]/25 font-mono"
                data-testid="godmode-code-input" />
              <Button onClick={handleGodmode} disabled={loading || !godmodeCode.trim()}
                className="bg-[#39FF14]/15 text-[#39FF14] hover:bg-[#39FF14]/25 border border-[#39FF14]/30 font-mono"
                data-testid="godmode-code-submit">GO</Button>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Pricing info */}
        <div className="text-center space-y-2">
          <div className="text-[9px] font-mono text-[#FF0000]/30 tracking-wider">SUBSCRIPTION PLANS</div>
          <div className="flex gap-2 justify-center">
            <div className="border border-[#FF0000]/15 px-4 py-2 text-center">
              <div className="text-sm font-mono font-bold text-[#FF0000]">$13</div>
              <div className="text-[8px] font-mono text-[#FF0000]/30">/month</div>
            </div>
            <div className="border border-[#FF0000]/25 px-4 py-2 text-center bg-[#FF0000]/5">
              <div className="text-sm font-mono font-bold text-[#FF0000]">$33</div>
              <div className="text-[8px] font-mono text-[#FF0000]/30">/lifetime</div>
            </div>
          </div>
          <div className="text-[8px] font-mono text-[#FF0000]/20">GODMODE LLC</div>
        </div>
      </div>
    </div>
  );
}
